import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { get, ref } from 'firebase/database';
import { Check, CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { auth, database } from '../services/firebase';
import {
  BASIC_PLAN,
  VOLUME_PRICING,
  estimateBasicPlanTotal,
  formatCurrencyFromCents,
  formatVolumeTierLabel,
} from '../config/billing';
import {
  cancelStripeSubscription,
  createBillingPortalSession,
  createCheckoutSession,
  redirectToCheckout,
} from '../services/stripeBilling';

type OrganizationRecord = {
  id: string;
  name: string;
  stripeId?: string | null;
  subscriptionId?: string | null;
};

type UserProfile = {
  needsOrganizationSetup?: boolean;
  organizationId?: string;
  organizationMembershipStatus?: string;
};

type SubscriptionPlanId = 'free' | 'basic' | 'addons';

type SubscriptionPlan = {
  id: SubscriptionPlanId;
  name: string;
  price: string;
  priceInterval?: string;
  description: string;
  features: string[];
  badge?: string;
  comingSoon?: boolean;
  selectable?: boolean;
};

type MembershipRecord = {
  role?: string | null;
  status?: string | null;
};

export default function Billing() {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('theme') === 'dark';
  });

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [organization, setOrganization] = useState<OrganizationRecord | null>(null);
  const [loadingOrganization, setLoadingOrganization] = useState(false);
  const [organizationError, setOrganizationError] = useState<string | null>(null);
  const [activeClinicsCount, setActiveClinicsCount] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanId>('free');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [cancelSubscriptionLoading, setCancelSubscriptionLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [membership, setMembership] = useState<MembershipRecord | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (!user) {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/billing' },
        });
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
      setIsDarkMode(true);
    }
    if (storedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    const profileRef = ref(database, `users/${currentUser.uid}`);
    void get(profileRef)
      .then(snapshot => {
        const value = snapshot.val() as UserProfile | null;
        if (value?.needsOrganizationSetup) {
          navigate('/organization/create', {
            replace: true,
            state: { returnTo: '/billing' },
          });
          return;
        }
        setProfile(value);
      })
      .catch(error => {
        console.warn('Failed to load users profile data', error);
      })
      .finally(() => {
        setProfileLoading(false);
      });
  }, [currentUser, navigate]);

  useEffect(() => {
    const userId = currentUser?.uid;
    if (!profile?.organizationId || !userId) {
      setMembership(null);
      setMembershipLoading(false);
      setMembershipError(null);
      return;
    }

    const membershipRef = ref(database, `organizationUsers/${profile.organizationId}/${userId}`);
    let isActive = true;

    setMembershipLoading(true);
    setMembershipError(null);

    void get(membershipRef)
      .then(snapshot => {
        if (!isActive) {
          return;
        }
        const value = snapshot.val() as MembershipRecord | null;
        if (!value) {
          setMembership(null);
          return;
        }
        setMembership({
          role: typeof value.role === 'string' ? value.role : null,
          status: typeof value.status === 'string' ? value.status : null,
        });
      })
      .catch(error => {
        if (!isActive) {
          return;
        }
        console.warn('Failed to load organization membership', error);
        setMembership(null);
        setMembershipError('We could not verify your access to billing. Please try again later.');
      })
      .finally(() => {
        if (isActive) {
          setMembershipLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [currentUser?.uid, profile?.organizationId]);

  useEffect(() => {
    if (!profile?.organizationId) {
      setOrganization(null);
      setSelectedPlan('free');
      setActiveClinicsCount(0);
      setLoadingOrganization(false);
      if (!profileLoading) {
        setOrganizationError(profile?.needsOrganizationSetup ? null : 'No organization details were found.');
      } else {
        setOrganizationError(null);
      }
      return;
    }

    const orgRef = ref(database, `organizations/${profile.organizationId}`);
    const clinicsRef = ref(database, `organizationLocations/${profile.organizationId}`);
    let isActive = true;

    setLoadingOrganization(true);
    setOrganizationError(null);

    void (async () => {
      try {
        const [organizationSnapshot, clinicsSnapshot] = await Promise.all([get(orgRef), get(clinicsRef)]);
        if (!isActive) {
          return;
        }

        const organizationValue = organizationSnapshot.val() as (OrganizationRecord & { clinicName?: string }) | null;
        if (!organizationValue) {
          setOrganization(null);
          setOrganizationError('No organization details were found.');
          setSelectedPlan('free');
          setActiveClinicsCount(0);
          return;
        }

        setOrganization({
          id: profile.organizationId as string,
          name: organizationValue.name ?? organizationValue.clinicName ?? '',
          stripeId: organizationValue.stripeId ?? null,
          subscriptionId: organizationValue.subscriptionId ?? null,
        });
        setSelectedPlan(organizationValue.subscriptionId ? 'basic' : 'free');

        const clinicsValue = clinicsSnapshot.val() as Record<string, { status?: string } | null> | null;
        if (!clinicsValue) {
          setActiveClinicsCount(0);
          return;
        }

        const activeClinics = Object.values(clinicsValue).reduce((count, clinicData) => {
          if (!clinicData) {
            return count;
          }
          const normalizedStatus =
            typeof clinicData.status === 'string'
              ? clinicData.status.toLowerCase()
              : 'active';
          return normalizedStatus === 'active' ? count + 1 : count;
        }, 0);

        setActiveClinicsCount(activeClinics);
      } catch (error) {
        if (!isActive) {
          return;
        }
        console.warn('Failed to load organization details', error);
        setOrganization(null);
        setOrganizationError('We were unable to load your organization details. Please try again later.');
        setSelectedPlan('free');
        setActiveClinicsCount(0);
      } finally {
        if (isActive) {
          setLoadingOrganization(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [profile?.organizationId, profile?.needsOrganizationSetup, profileLoading]);

  const cardClass = `${isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} rounded-lg p-8 ring-1`;
  const mutedTextClass = `${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`;
  const primaryActionButtonClass = `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition ${isDarkMode ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`;
  const secondaryActionButtonClass = `inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${isDarkMode ? 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'}`;
  const loaderTextClass = isDarkMode ? 'text-slate-200' : 'text-slate-600';
  const normalizedMembershipRole =
    typeof membership?.role === 'string' ? membership.role.trim().toLowerCase() : '';
  const normalizedMembershipStatus =
    typeof membership?.status === 'string' ? membership.status.trim().toLowerCase() : '';
  const normalizedProfileMembershipStatus =
    typeof profile?.organizationMembershipStatus === 'string'
      ? profile.organizationMembershipStatus.trim().toLowerCase()
      : '';
  const hasOwnerAccess =
    Boolean(profile?.organizationId) &&
    (normalizedMembershipRole === 'owner' || normalizedMembershipRole === 'administrator') &&
    (normalizedMembershipStatus ? normalizedMembershipStatus === 'active' : true) &&
    (normalizedProfileMembershipStatus ? normalizedProfileMembershipStatus === 'active' : true);

  const billingSummary = useMemo(
    () => estimateBasicPlanTotal(activeClinicsCount),
    [activeClinicsCount],
  );
  const selectedPlanIsBasic = selectedPlan === 'basic';
  const hasSubscription = Boolean(organization?.subscriptionId);
  const volumeTierDescriptions = useMemo(
    () =>
      VOLUME_PRICING.map(tier => {
        const rangeLabel = formatVolumeTierLabel(tier);
        const amount = formatCurrencyFromCents(tier.pricePerClinicCents);
        return `${rangeLabel}: ${amount} each / month`;
      }),
    [],
  );

  const basicPlanFeatures = useMemo(
    () => [...BASIC_PLAN.features.map(feature => feature.label), ...volumeTierDescriptions],
    [volumeTierDescriptions],
  );

  const subscriptionPlans: SubscriptionPlan[] = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      priceInterval: '/month',
      description: 'Ideal for independent locations exploring Happy Tail Tracker.',
      features: [
        'Leave comments on pet summary',
        'Download pet summary',
      ],
      badge: selectedPlan === 'free' && !organization?.subscriptionId ? 'Current plan' : undefined,
    },
    {
      id: 'basic',
      name: 'Premium',
      price: formatCurrencyFromCents(BASIC_PLAN.monthlyPriceCents),
      priceInterval: '/month',
      description: `Includes ${BASIC_PLAN.includedClinics} location. Additional locations follow tiered pricing.`,
      features: basicPlanFeatures,
      badge: organization?.subscriptionId ? 'Current plan' : undefined,
    },
  ];

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
    });
  };

  const handleSignIn = () => {
    navigate('/signin', { state: { returnTo: '/billing' } });
  };

  const handleCreateAccount = () => {
    navigate('/register', { state: { returnTo: '/billing' } });
  };

  const handleLogout = () => {
    void signOut(auth)
      .then(() => {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/billing' },
        });
      })
      .catch(error => {
        console.warn('Failed to sign out', error);
      });
  };

  const handleDowngradeToFreePlan = async () => {
    if (!organization) {
      setBillingError('We could not determine your organization. Please refresh and try again.');
      return;
    }

    if (!currentUser) {
      setBillingError('You need to be signed in to manage billing.');
      return;
    }

    setCancelSubscriptionLoading(true);
    setBillingError(null);

    try {
      await cancelStripeSubscription({ organizationId: organization.id });
      setOrganization(prev =>
        prev
          ? {
              ...prev,
              subscriptionId: null,
            }
          : prev,
      );
      setSelectedPlan('free');
    } catch (error) {
      console.warn('Failed to cancel Stripe subscription', error);
      setBillingError(
        error instanceof Error
          ? error.message
          : 'We were unable to cancel the subscription. Please try again.',
      );
    } finally {
      setCancelSubscriptionLoading(false);
    }
  };

  const handleStartCheckout = async () => {
    if (!organization) {
      setBillingError('We could not determine your organization. Please refresh and try again.');
      return;
    }
    if (!currentUser) {
      setBillingError('You need to be signed in to start checkout.');
      return;
    }
    if (!selectedPlanIsBasic) {
      setBillingError('Select the Basic plan to continue with billing.');
      return;
    }

    setCheckoutLoading(true);
    setBillingError(null);

    try {
      const origin = window.location.origin;
      const successUrl = `${origin}/billing?billing=success`;
      const cancelUrl = `${origin}/billing?billing=cancel`;
      const checkoutSession = await createCheckoutSession({
        userId: currentUser.uid,
        organizationId: organization.id,
        planId: 'basic',
        activeClinics: Math.max(activeClinicsCount, 1),
        successUrl,
        cancelUrl,
        customerEmail: currentUser.email ?? undefined,
      });

      await redirectToCheckout(checkoutSession);
    } catch (error) {
      console.warn('Failed to start Stripe checkout', error);
      setBillingError(
        error instanceof Error
          ? error.message
          : 'We were unable to start checkout. Please try again.',
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    if (!organization) {
      setBillingError('We could not determine your organization. Please refresh and try again.');
      return;
    }
    if (!currentUser) {
      setBillingError('You need to be signed in to manage billing.');
      return;
    }

    if (!organization.stripeId) {
      setBillingError('We could not find a Stripe customer for your organization yet.');
      return;
    }

    setBillingPortalLoading(true);
    setBillingError(null);

    try {
      const returnUrl = window.location.href;
      const portalSession = await createBillingPortalSession({
        userId: currentUser.uid,
        returnUrl,
        customerEmail: currentUser.email ?? undefined,
      });

      if (portalSession.url) {
        window.location.assign(portalSession.url);
        return;
      }

      throw new Error('Stripe did not return a billing portal URL.');
    } catch (error) {
      console.warn('Failed to open Stripe billing portal', error);
      setBillingError(
        error instanceof Error
          ? error.message
          : 'We were unable to open the billing portal. Please try again.',
      );
    } finally {
      setBillingPortalLoading(false);
    }
  };

  const hasOrganizationAccess = Boolean(profile?.organizationId);
  const isContentLoading =
    profileLoading || (profile?.organizationId ? loadingOrganization || membershipLoading : false);

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900 text-slate-100">
        <div className="flex items-center gap-3 text-lg font-semibold">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Preparing billing...
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  if (
    profile?.organizationId &&
    !isContentLoading &&
    (!hasOwnerAccess || Boolean(membershipError))
  ) {
    return (
      <DashboardLayout
        isDarkMode={isDarkMode}
        onToggleTheme={handleThemeToggle}
        currentUser={currentUser}
        onSignIn={handleSignIn}
        onCreateAccount={handleCreateAccount}
        onLogout={handleLogout}
        needsOrganizationSetup={profile?.needsOrganizationSetup}
        organizationMembershipStatus={profile?.organizationMembershipStatus}
        hasOrganizationAccess={hasOrganizationAccess}
      >
        <div className="space-y-6">
          <section className={cardClass}>
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold">Billing access restricted</h1>
            </div>

            {/* Subtitle below both */}
            <p className={`${mutedTextClass} mt-3`}>
              Only users with Owner or Administrator role can make changes to billing.
            </p>
          </section>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      isDarkMode={isDarkMode}
      onToggleTheme={handleThemeToggle}
      currentUser={currentUser}
      onSignIn={handleSignIn}
      onCreateAccount={handleCreateAccount}
      onLogout={handleLogout}
      needsOrganizationSetup={profile?.needsOrganizationSetup}
      organizationMembershipStatus={profile?.organizationMembershipStatus}
      hasOrganizationAccess={hasOrganizationAccess}
    >
      {isContentLoading ? (
        <div className="flex justify-center pt-12">
          <div className={`flex items-center gap-3 text-sm font-semibold ${loaderTextClass}`}>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading billing details...
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <section className={cardClass}>
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold">Manage your subscription</h1>


            </div>

            {/* Subtitle below both */}
            <p className={`${mutedTextClass} mt-3`}>
              Select the plan that fits your clinic and manage billing through Stripe.
            </p>

            {organizationError ? (
              <div
                className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                  isDarkMode
                    ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                    : 'border-amber-500/30 bg-amber-50 text-amber-700'
                }`}
              >
                {organizationError}
              </div>
            ) : null}

            {organization ? (
              <>
                <div className="mt-10 grid gap-4 md:grid-cols-2">
                  {subscriptionPlans.map(plan => {
                    const isSelected = selectedPlan === plan.id;
                    const baseBackgroundClass = isDarkMode
                      ? 'bg-slate-900/70 hover:bg-slate-900/60'
                      : 'bg-white hover:bg-slate-50';
                    const borderClass = isSelected
                      ? 'border-blue-500 ring-2 ring-blue-500'
                      : isDarkMode
                        ? 'border-slate-800'
                        : 'border-slate-200';
                    const sharedContent = (
                      <>
                        {plan.badge && (
                          <span className="absolute right-4 top-4 rounded-full bg-blue-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-500">
                            {plan.badge}
                          </span>
                        )}
                        {plan.comingSoon && (
                          <span className="absolute right-4 top-4 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-500">
                            Coming soon
                          </span>
                        )}
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-3">
                            <h3 className="text-lg font-semibold">{plan.name}</h3>
                            <div>
                              <span className="text-3xl font-bold">{plan.price}</span>
                              {plan.priceInterval ? (
                                <span className={`ml-1 text-sm font-medium ${mutedTextClass}`}>
                                  {plan.priceInterval}
                                </span>
                              ) : null}
                            </div>
                            <p className={`${mutedTextClass} text-sm`}>{plan.description}</p>
                          </div>
                        </div>
                        <ul className="space-y-2 text-sm">
                          {plan.features.map(feature => (
                            <li key={feature} className="flex items-center gap-2">
                              <Check className="h-4 w-4 text-blue-500" aria-hidden="true" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    );

                    if (plan.selectable === false) {
                      return (
                        <div
                          key={plan.id}
                          className={`${baseBackgroundClass} ${borderClass} relative flex flex-col gap-6 rounded-2xl border p-6 text-left`}
                        >
                          {sharedContent}
                        </div>
                      );
                    }

                    return (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => setSelectedPlan(plan.id)}
                        aria-pressed={isSelected}
                        className={`${baseBackgroundClass} ${borderClass} relative flex flex-col gap-6 rounded-2xl border p-6 text-left transition focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300/60`}
                      >
                        {sharedContent}
                      </button>
                    );
                  })}
                </div>

                <div
                  className={`mt-8 space-y-4 rounded-xl border px-4 py-5 text-sm ${
                    isDarkMode
                      ? 'border-slate-800 bg-slate-900/70 text-slate-200'
                      : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}
                >
                  {billingError ? (
                    <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-500">
                      {billingError}
                    </div>
                  ) : null}

                  {selectedPlan === 'free' && (
                    <div className="space-y-3">
                      <p>
                        {hasSubscription
                          ? "You're on the Premium plan. Manage billing in Stripe anytime."
                          : "You're on the Free plan. Upgrade anytime to unlock automations and connected billing."}
                      </p>
                      {hasSubscription ? (
                        <div className="space-y-3">
                          <p className={`${mutedTextClass} text-xs`}>
                            Switching to the Free plan cancels your current Stripe subscription immediately.
                            You can re-upgrade at any time.
                          </p>
                          <div className="flex flex-wrap gap-3">
                            <button
                              type="button"
                              className={primaryActionButtonClass}
                              onClick={handleDowngradeToFreePlan}
                              disabled={cancelSubscriptionLoading}
                            >
                              {cancelSubscriptionLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                              ) : (
                                <Check className="h-4 w-4" aria-hidden="true" />
                              )}
                              Switch to Free plan
                            </button>
                            <button
                              type="button"
                              className={secondaryActionButtonClass}
                              onClick={handleOpenBillingPortal}
                              disabled={billingPortalLoading || cancelSubscriptionLoading}
                            >
                              {billingPortalLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                              ) : (
                                <ExternalLink className="h-4 w-4" aria-hidden="true" />
                              )}
                              Review billing in Stripe
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}

                  {selectedPlanIsBasic && (
                    <div className="space-y-4">
                      <div
                        className={`rounded-xl border border-dashed px-4 py-4 ${
                          isDarkMode ? 'border-blue-500/40 bg-blue-500/10' : 'border-blue-500/30 bg-blue-50'
                        }`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 text-sm font-semibold">
                          <span>Active locations</span>
                          <span>{billingSummary.activeClinics}</span>
                        </div>
                        <div className="mt-3 space-y-2 text-sm">
                          <div className="flex items-center justify-between gap-2">
                            <span>Price per location</span>
                            <span>{formatCurrencyFromCents(billingSummary.perClinicCents)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2 text-base font-semibold">
                            <span>Estimated monthly total</span>
                            <span>{formatCurrencyFromCents(billingSummary.totalCents)}</span>
                          </div>
                        </div>
                        <p className={`mt-3 text-xs ${mutedTextClass}`}>
                          Current tier: {formatVolumeTierLabel(billingSummary.tier)}.
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          className={primaryActionButtonClass}
                          onClick={hasSubscription ? handleOpenBillingPortal : handleStartCheckout}
                          disabled={hasSubscription ? billingPortalLoading : checkoutLoading}
                        >
                          {hasSubscription ? (
                            billingPortalLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            ) : (
                              <ExternalLink className="h-4 w-4" aria-hidden="true" />
                            )
                          ) : checkoutLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                          ) : (
                            <CreditCard className="h-4 w-4" aria-hidden="true" />
                          )}
                          {hasSubscription ? 'Manage billing in Stripe' : 'Start Stripe checkout'}
                        </button>
                        {!hasSubscription && (
                          <button
                            type="button"
                            className={secondaryActionButtonClass}
                            onClick={handleOpenBillingPortal}
                            disabled={billingPortalLoading}
                          >
                            {billingPortalLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            ) : (
                              <ExternalLink className="h-4 w-4" aria-hidden="true" />
                            )}
                            Already subscribed? Access portal
                          </button>
                        )}
                      </div>

                      <p className={`${mutedTextClass} text-xs`}>
                        Stripe securely handles checkout and billing management. Additional clinics are
                        billed automatically using the tiered pricing above.
                      </p>
                    </div>
                  )}

                  {selectedPlan === 'addons' && (
                    <p>
                      Add-on pricing activates automatically once you upgrade to the Basic plan. Active
                      clinics beyond the first location follow the tiered rates above.
                    </p>
                  )}
                </div>
              </>
            ) : null}
          </section>
        </div>
      )}
    </DashboardLayout>
  );
}
